import React, { useState, Component } from 'react'
import { Component, Zone } from '../../utils/types'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
interface ProductCardProps {
  component: Component
  zones: Zone[]
  onAddComponent: (component: Component, targetZone: Zone | 'all') => void
}
export function ProductCard({
  component,
  zones,
  onAddComponent,
}: ProductCardProps) {
  const [showZoneDropdown, setShowZoneDropdown] = useState(false)
  // Format currency
  const formatCurrency = (amount: number) => {
    return (
      new Intl.NumberFormat('da-DK', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount) + ' kr'
    )
  }
  const handleZoneSelect = (zone: Zone | 'all') => {
    onAddComponent(component, zone)
    setShowZoneDropdown(false)
  }
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-4">
        {/* Brand and model */}
        <div className="mb-2">
          <div className="flex justify-between items-start">
            <div>
              <span className="text-sm font-medium text-gray-900">
                {component.brand}
              </span>
              <h3 className="text-base font-medium text-gray-900">
                {component.model}
              </h3>
            </div>
            {component.inStock && (
              <div className="bg-green-50 px-2 py-0.5 rounded text-xs text-green-700">
                På lager
              </div>
            )}
          </div>
        </div>
        {/* Key data */}
        <div className="mb-3">
          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
            {component.keyData.map((data, index) => (
              <div key={index} className="flex items-center">
                <span className="w-2 h-2 bg-gray-300 rounded-full mr-1.5"></span>
                {data}
              </div>
            ))}
          </div>
        </div>
        {/* Price and ETA */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-lg font-medium text-gray-900">
            {formatCurrency(component.price)}
          </div>
          <div className="text-xs text-gray-600">ETA: {component.eta}</div>
        </div>
        {/* Badges */}
        {component.badges && component.badges.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {component.badges.map((badge, index) => {
              let bgColor = 'bg-gray-100'
              let textColor = 'text-gray-700'
              if (badge === 'Grøn') {
                bgColor = 'bg-emerald-50'
                textColor = 'text-emerald-700'
              } else if (badge === 'Anbefalet') {
                bgColor = 'bg-blue-50'
                textColor = 'text-blue-700'
              }
              return (
                <div
                  key={index}
                  className={`px-2 py-0.5 rounded text-xs ${bgColor} ${textColor}`}
                >
                  {badge}
                </div>
              )
            })}
          </div>
        )}
        {/* Add to zone button */}
        <div className="relative">
          <button
            className="w-full bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-center"
            onClick={() => setShowZoneDropdown(!showZoneDropdown)}
          >
            <span>Vælg til zone</span>
            <ChevronDownIcon className="h-4 w-4 ml-1" />
          </button>
          {showZoneDropdown && (
            <div className="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1">
              <button
                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                onClick={() => handleZoneSelect('all')}
              >
                <span className="mr-2">Alle vådrum</span>
              </button>
              {zones.map((zone) => (
                <button
                  key={zone.id}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  onClick={() => handleZoneSelect(zone)}
                >
                  <span>{zone.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
